import { Thead, Th, Tr, Flex } from '@wegroup/design-system';
import { flexRender, HeaderGroup, Header } from '@tanstack/react-table';
import SortIndicator, { SortType } from '../SortIndicator';

interface CustomColumnProps {
  isSticky?: boolean;
}

interface Props<TData> {
  headerGroups: HeaderGroup<TData>[];
  isHeaderSticky?: boolean;
  isScrollable?: boolean;
}

const ReactTableV8Head = <TData,>({
  headerGroups,
  isHeaderSticky,
  isScrollable,
}: Props<TData>) => {
  const getSortType = (header: Header<TData, unknown>) => {
    const sortDirection = header.column.getIsSorted();
    if (sortDirection === 'asc') {
      return SortType.Asc;
    }
    if (sortDirection === 'desc') {
      return SortType.Desc;
    }
    return undefined;
  };

  return (
    <Thead position={isHeaderSticky ? 'sticky' : 'unset'} top="0" zIndex={1}>
      {headerGroups.map((headerGroup) => (
        <Tr key={headerGroup.id}>
          {headerGroup.headers.map((header) => {
            const columnMeta = header.column.columnDef.meta as
              | CustomColumnProps
              | undefined;
            const isStickyColumn = !!columnMeta?.isSticky && isScrollable;
            return (
              <Th
                key={header.id}
                position={isStickyColumn ? 'sticky' : 'unset'}
                zIndex={isStickyColumn ? '1' : 'unset'}
                transition="0.2s"
                _last={
                  isStickyColumn
                    ? {
                        right: 0,
                        borderLeft: '1px',
                        borderColor: 'gray.50',
                        boxShadow: '-1px 0 10px -2px rgba(40, 41, 61, 0.08)',
                      }
                    : {}
                }
                _first={
                  isStickyColumn
                    ? {
                        left: '0',
                        borderRight: '1px',
                        boxShadow: '1px 0 10px -2px rgba(40, 41, 61, 0.08)',
                        borderColor: 'gray.50',
                      }
                    : {}
                }
                sx={{
                  '@media print': {
                    color: 'black !important',
                  },
                }}
                {...{
                  onClick: header.column.getCanSort()
                    ? header.column.getToggleSortingHandler()
                    : undefined,
                  style: {
                    cursor: header.column.getCanSort() ? 'pointer' : 'default',
                  },
                }}
              >
                <Flex>
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                  {header.column.getCanSort() && (
                    <SortIndicator type={getSortType(header)} />
                  )}
                </Flex>
              </Th>
            );
          })}
        </Tr>
      ))}
    </Thead>
  );
};

export default ReactTableV8Head;
